import React, { useState, useEffect, useRef } from "react";
import { Input, LabelWithContainer, Label, ErrorMessage, SuggestionsList } from "./FormStyles";
import Spinner from "../Common/Spinner";
import CheckRender from "../Common/CheckRender";
import useEmpresa from "../../hooks/useEmpresa";
import {
  validationSchemaRequired,
  validationSchemaEmail,
  validationSchemaPhone,
  validationSchemaCustom,
  validationSchemaMedicoPrescriptor,
} from "../../utils/SchemaValidations";
import { isEmptyValue } from "../../utils/StringUtil";

const CustomInput = ({
  type,
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  handleKeyPress,
  fontSize,
  borderRadius,
  responsiveFontSize,
  responsivePadding,
  width,
  height,
  maxLength,
  disabled,
  required,
  isLabelWithContainer,
  labelWidth,
  labelHeight,
  validationConfig,
  showRequired,
  suggestions,
  setSelectedSuggestion,
  dataLoading,
  updatedSuggestions,
  minDate,
  maxDate,
  validationDebounceMs = 500,
  errorMessage,
  testid
}) => {
  const empresa = useEmpresa();

  const [errors, setErrors] = useState({});

  const [touched, setTouched] = useState(false);

  const [filteredSuggestions, setFilteredSuggestions] = useState([]);

  const [showSuggestions, setShowSuggestions] = useState(false);

  const inputRef = useRef(null);

  const validationTimeoutRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (required && showRequired && isEmptyValue(value)) {
      requireField()
    }
  }, [showRequired]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setShowSuggestions(updatedSuggestions?.length > 0);
    setFilteredSuggestions(updatedSuggestions);
  }, [updatedSuggestions]); // eslint-disable-line react-hooks/exhaustive-deps

  const requireField = async () => {
    try {
      await validationSchemaRequired.validate({
        requiredField: null,
      });
      setErrors({});
    } catch (error) {
      setErrors({ [error.path]: error.message });
    }
  };

  const validate = async (valueToValidate) => {
    const valueToCheck = valueToValidate !== undefined ? valueToValidate : value;

    try {
      if (!touched && valueToValidate === undefined) {
        return;
      }

      if (required) {
        await validationSchemaRequired.validate({
          requiredField: valueToCheck,
        });
      }

      if (validationConfig) {
        await validationSchemaCustom(
          validationConfig?.validationCallBack,
          validationConfig?.errorMessage
        ).validate({
          customField: valueToCheck,
        });
      }

      switch (type) {
        case "email":
          await validationSchemaEmail.validate({
            emailField: valueToCheck,
          });
          break;
        case "phone":
          await validationSchemaPhone.validate({
            phoneField: valueToCheck,
          });
          break;
        case "medicoPrescriptor":
          await validationSchemaMedicoPrescriptor.validate({
            medicoPrescriptorField: valueToCheck,
          });
          break;
        case "medicoPrescriptor":
          await validationSchemaMedicoPrescriptor.validate({
            medicoPrescriptorField: value,
          });
          break;
        default:
          break;
      }

      setErrors({});
    } catch (error) {
      setErrors({ [error.path]: error.message });
    }
  };

  const handleChange = (event) => {
    const inputValue = event?.target?.value;

    setTouched(true);

    onChange(inputValue);

    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }
    validationTimeoutRef.current = setTimeout(() => {
      validate(inputValue);
      validationTimeoutRef.current = null;
    }, validationDebounceMs);

    if (suggestions?.length > 0) {
      const filtered = suggestions.filter((item) => {
        const lowerCaseInput = inputValue?.toLowerCase();
        const lowerCaseCodigo = item?.codigo?.toLowerCase();
        const lowerCaseDescripcion = item?.descripcion?.toLowerCase();

        return (
          lowerCaseCodigo?.includes(lowerCaseInput) ||
          lowerCaseDescripcion?.includes(lowerCaseInput)
        );
      });
      setFilteredSuggestions(filtered);
      setShowSuggestions(!isEmptyValue(inputValue));
    }

  };

  const handleSelect = (item) => {
    setSelectedSuggestion(item);
    onChange(item?.descripcion, false);
    setShowSuggestions(false);
    setTouched(true);
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
      validationTimeoutRef.current = null;
    }
    validate(item?.descripcion);
  };

  return (
    <>
      <CheckRender allowed={label}>
        <CheckRender allowed={isLabelWithContainer}>
          <div>
            <LabelWithContainer
              font={empresa?.LETRA_PRINCIPAL}
              labelWidth={labelWidth}
              labelHeight={labelHeight}
            >
              {required ? `${label} *` : label}
            </LabelWithContainer>
          </div>
        </CheckRender>
        <CheckRender allowed={!isLabelWithContainer}>
          <Label font={empresa?.LETRA_PRINCIPAL}>{required ? `${label} *` : label}</Label>
        </CheckRender>
      </CheckRender>

      <Input
        ref={inputRef}
        type={type}
        placeholder={placeholder}
        fontSize={fontSize}
        borderRadius={borderRadius}
        responsiveFontSize={responsiveFontSize}
        responsivePadding={responsivePadding}
        value={value || ""}
        onChange={handleChange}
        onKeyDown={handleKeyPress}
        onBlur={(e) => {
          validate();
          if (typeof onBlur === "function") {
            onBlur(e);
          }
        }}
        font={empresa?.LETRA_PRINCIPAL}
        width={width}
        height={height}
        disabled={disabled}
        maxLength={maxLength}
        testid={testid}
        minDate={minDate}
        maxDate={maxDate}
      />

      {errors.requiredField && (
        <ErrorMessage>{errors.requiredField}</ErrorMessage>
      )}
      {errors.emailField && <ErrorMessage>{errors.emailField}</ErrorMessage>}
      {errors.phoneField && <ErrorMessage>{errors.phoneField}</ErrorMessage>}
      {errors.medicoPrescriptorField && (
        <ErrorMessage>{errors.medicoPrescriptorField}</ErrorMessage>
      )}
      {errors.customField && <ErrorMessage>{errors.customField}</ErrorMessage>}
      {errorMessage && !errors.requiredField && !errors.emailField && !errors.phoneField && !errors.customField && (
        <ErrorMessage>{errorMessage}</ErrorMessage>
      )}

      <CheckRender allowed={dataLoading}>
        <Spinner
          size={20}
          colorBorderTop={empresa?.COLOR_PRINCIPAL}
        />
      </CheckRender>

      <CheckRender allowed={!dataLoading}>
        {showSuggestions && (
          <SuggestionsList hoverBgColor={empresa?.COLOR_PRINCIPAL}>
            {filteredSuggestions.map((item) => (
              <li key={item.codigo+item.descripcion} onClick={() => handleSelect(item)}>
                {`${item.codigo} - ${item.descripcion}`}
              </li>
            ))}
          </SuggestionsList>
        )}
      </CheckRender>
    </>
  );
};

export default CustomInput;